import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import AdminSidebar from "@/pages/admin/dashboard/components/AdminSidebar";

const BUCKET_NAME = "photos";
const STORAGE_FOLDER = "public";

interface PhotoSlide {
  id: string;
  image_url: string;
  alt_text: string;
  caption_en: string;
  caption_ps: string;
  caption_da: string;
  sort_order: number;
  is_active: boolean;
}

const emptyPhoto: PhotoSlide = {
  id: "",
  image_url: "",
  alt_text: "",
  caption_en: "",
  caption_ps: "",
  caption_da: "",
  sort_order: 0,
  is_active: true,
};

export default function AdminPhotosPage() {
  const [photos, setPhotos] = useState<PhotoSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<PhotoSlide | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [form, setForm] = useState<PhotoSlide>(emptyPhoto);
  const [successMsg, setSuccessMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const fetchPhotos = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const { data, error: fetchErr } = await supabase
        .from("photo_slider")
        .select("*")
        .order("sort_order", { ascending: true });

      if (fetchErr) throw fetchErr;
      setPhotos((data as PhotoSlide[]) ?? []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to load photos";
      setError(message);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be under 10MB.");
      return;
    }
    setUploadFile(file);
    setUploadPreview(URL.createObjectURL(file));
    setError("");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const uploadImageToStorage = async (): Promise<string | null> => {
    if (!uploadFile) return null;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const fileExt = uploadFile.name.split(".").pop() || "jpg";
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      const filePath = `${STORAGE_FOLDER}/${fileName}`;

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, uploadFile, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadErr) throw uploadErr;

      setUploadProgress(100);

      const { data: urlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setError(message);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const extractStoragePath = (url: string): string | null => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split("/");
      const bucketIndex = pathParts.indexOf(BUCKET_NAME);
      if (bucketIndex === -1) return null;
      return pathParts.slice(bucketIndex + 1).join("/");
    } catch {
      return null;
    }
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditing(null);
    setForm({ ...emptyPhoto, sort_order: photos.length });
    setUploadFile(null);
    setUploadPreview("");
    setUploadProgress(0);
  };

  const handleEdit = (photo: PhotoSlide) => {
    setEditing(photo);
    setIsAdding(false);
    setForm({ ...photo });
    setUploadFile(null);
    setUploadPreview("");
    setUploadProgress(0);
  };

  const handleDelete = async (id: string) => {
    try {
      const photo = photos.find((p) => p.id === id);
      setError("");

      if (photo) {
        // Try to delete from storage if it's a Supabase-hosted image
        const storagePath = extractStoragePath(photo.image_url);
        if (storagePath) {
          await supabase.storage.from(BUCKET_NAME).remove([storagePath]);
        }
      }

      const { error: deleteErr } = await supabase
        .from("photo_slider")
        .delete()
        .eq("id", id);

      if (deleteErr) throw deleteErr;

      setPhotos((prev) => prev.filter((p) => p.id !== id));
      showSuccess("Photo deleted successfully.");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Delete failed";
      setError(message);
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      setError("");
      const photo = photos.find((p) => p.id === id);
      if (!photo) return;

      const newActive = !photo.is_active;

      const { error: updateErr } = await supabase
        .from("photo_slider")
        .update({ is_active: newActive })
        .eq("id", id);

      if (updateErr) throw updateErr;

      setPhotos((prev) =>
        prev.map((p) => (p.id === id ? { ...p, is_active: newActive } : p))
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Toggle failed";
      setError(message);
    }
  };

  const handleSave = async () => {
    if (!uploadFile && !form.image_url.trim()) {
      setError("Please upload an image or enter an image URL.");
      return;
    }
    if (!form.caption_en.trim()) {
      setError("English caption is required.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      let imageUrl = form.image_url;

      // If there's a file to upload, do it first
      if (uploadFile) {
        const uploadedUrl = await uploadImageToStorage();
        if (!uploadedUrl) {
          setSaving(false);
          return;
        }
        imageUrl = uploadedUrl;
      }

      if (isAdding) {
        const { data, error: insertErr } = await supabase
          .from("photo_slider")
          .insert({
            image_url: imageUrl,
            alt_text: form.alt_text,
            caption_en: form.caption_en,
            caption_ps: form.caption_ps,
            caption_da: form.caption_da,
            sort_order: form.sort_order,
            is_active: form.is_active,
          })
          .select()
          .single();

        if (insertErr) throw insertErr;

        if (data) {
          setPhotos((prev) =>
            [...prev, data as PhotoSlide].sort((a, b) => a.sort_order - b.sort_order)
          );
        }
        showSuccess("Photo added successfully!");
      } else if (editing) {
        const { error: updateErr } = await supabase
          .from("photo_slider")
          .update({
            image_url: imageUrl,
            alt_text: form.alt_text,
            caption_en: form.caption_en,
            caption_ps: form.caption_ps,
            caption_da: form.caption_da,
            sort_order: form.sort_order,
            is_active: form.is_active,
          })
          .eq("id", editing.id);

        if (updateErr) throw updateErr;

        setPhotos((prev) =>
          prev
            .map((p) =>
              p.id === editing.id
                ? { ...p, image_url: imageUrl, alt_text: form.alt_text, caption_en: form.caption_en, caption_ps: form.caption_ps, caption_da: form.caption_da, sort_order: form.sort_order, is_active: form.is_active }
                : p
            )
            .sort((a, b) => a.sort_order - b.sort_order)
        );
        showSuccess("Photo updated successfully!");
      }

      setIsAdding(false);
      setEditing(null);
      setForm(emptyPhoto);
      setUploadFile(null);
      setUploadPreview("");
      setUploadProgress(0);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Save failed";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditing(null);
    setForm(emptyPhoto);
    setUploadFile(null);
    setUploadPreview("");
    setUploadProgress(0);
    setError("");
  };

  const movePhoto = async (id: string, direction: "up" | "down") => {
    try {
      setError("");
      const sorted = [...photos].sort((a, b) => a.sort_order - b.sort_order);
      const index = sorted.findIndex((p) => p.id === id);
      if (index === -1) return;
      if (direction === "up" && index === 0) return;
      if (direction === "down" && index === sorted.length - 1) return;

      const swapIndex = direction === "up" ? index - 1 : index + 1;
      const photoA = sorted[index];
      const photoB = sorted[swapIndex];

      await supabase
        .from("photo_slider")
        .update({ sort_order: photoB.sort_order })
        .eq("id", photoA.id);

      await supabase
        .from("photo_slider")
        .update({ sort_order: photoA.sort_order })
        .eq("id", photoB.id);

      const newSorted = [...sorted];
      [newSorted[index], newSorted[swapIndex]] = [newSorted[swapIndex], newSorted[index]];
      setPhotos(newSorted.map((p, i) => ({ ...p, sort_order: i })));
      showSuccess("Order updated.");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Reorder failed";
      setError(message);
    }
  };

  const showForm = isAdding || editing;
  const previewSrc = uploadPreview || form.image_url;

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#F5F7FA]">
        <AdminSidebar />
        <div className="flex-1 p-6 max-w-6xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-500 text-sm">Loading photos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>Photo Slider</h1>
              <p className="text-gray-500 text-sm mt-1">
                Manage the photo slider displayed on the homepage. Upload, edit, and reorder photos.
              </p>
            </div>
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-lg font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Add Photo
            </button>
          </div>

          {error && (
            <div className="mb-6 px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-sm flex items-start gap-3">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
              <span>{error}</span>
              <button onClick={() => setError("")} className="ml-auto text-rose-400 hover:text-rose-600 cursor-pointer whitespace-nowrap">
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {successMsg && (
            <div className="mb-6 px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-sm">
              {successMsg}
            </div>
          )}

          {/* Form */}
          {showForm && (
            <div className="bg-white border border-gray-100/70 rounded-xl p-6 mb-8">
              <h2 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                {isAdding ? "Add New Photo" : "Edit Photo"}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Upload area */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-foreground-950 mb-1.5">
                    Upload Image
                  </label>

                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                      dragOver
                        ? "border-[#3B9EF5] bg-[#3B9EF5]/10"
                        : "border-gray-200/60 hover:border-gray-300 hover:bg-[#FAF8F5]"
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileSelect(file);
                        e.target.value = "";
                      }}
                      className="hidden"
                    />

                    {uploadPreview ? (
                      <div className="relative w-full aspect-[16/7] rounded-lg overflow-hidden">
                        <img
                          src={uploadPreview}
                          alt="Upload preview"
                          className="w-full h-full object-cover object-top rounded-lg"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setUploadFile(null);
                            setUploadPreview("");
                            setUploadProgress(0);
                          }}
                          className="absolute top-3 right-3 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center cursor-pointer transition-colors whitespace-nowrap"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    ) : form.image_url ? (
                      <div className="relative w-full aspect-[16/7] rounded-lg overflow-hidden">
                        <img
                          src={form.image_url}
                          alt={form.alt_text || "Current image"}
                          className="w-full h-full object-cover object-top rounded-lg"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="py-8">
                        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#FAF8F5] mx-auto mb-3">
                          <i className="ri-image-add-line text-2xl text-gray-400"></i>
                        </div>
                        <p className="text-foreground-700 font-medium text-sm">Click to browse or drag & drop</p>
                        <p className="text-gray-400 text-xs mt-1">PNG, JPG, WebP up to 10MB</p>
                      </div>
                    )}
                  </div>

                  {isUploading && (
                    <div className="mt-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-[#FAF8F5] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#3B9EF5] rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap">Uploading...</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* URL fallback */}
                <div className="md:col-span-2">
                  <div className="relative flex items-center gap-3 mb-3">
                    <div className="flex-1 h-px bg-gray-100/70"></div>
                    <span className="text-xs text-gray-400 font-medium px-2">OR paste a URL</span>
                    <div className="flex-1 h-px bg-gray-100/70"></div>
                  </div>
                  <input
                    type="text"
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-4 py-2.5 border border-gray-100/70 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground-950 mb-1.5">
                    Alt Text
                  </label>
                  <input
                    type="text"
                    value={form.alt_text}
                    onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
                    placeholder="Describe the image..."
                    className="w-full px-4 py-2.5 border border-gray-100/70 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground-950 mb-1.5">
                    Caption (English) *
                  </label>
                  <input
                    type="text"
                    value={form.caption_en}
                    onChange={(e) => setForm({ ...form, caption_en: e.target.value })}
                    placeholder="Caption in English..."
                    className="w-full px-4 py-2.5 border border-gray-100/70 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground-950 mb-1.5">
                    Caption (Pashto)
                  </label>
                  <input
                    type="text"
                    value={form.caption_ps}
                    onChange={(e) => setForm({ ...form, caption_ps: e.target.value })}
                    placeholder="Caption in Pashto..."
                    className="w-full px-4 py-2.5 border border-gray-100/70 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors bg-white text-right"
                    dir="rtl"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground-950 mb-1.5">
                    Caption (Dari)
                  </label>
                  <input
                    type="text"
                    value={form.caption_da}
                    onChange={(e) => setForm({ ...form, caption_da: e.target.value })}
                    placeholder="Caption in Dari..."
                    className="w-full px-4 py-2.5 border border-gray-100/70 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-400 transition-colors bg-white text-right"
                    dir="rtl"
                  />
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-200/60 text-primary-500 focus:ring-primary-400"
                    />
                    <span className="text-sm text-foreground-950 font-medium">Visible on site</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-lg font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {saving && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {saving ? "Saving..." : isAdding ? "Add Photo" : "Save Changes"}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="px-6 py-2.5 bg-[#FAF8F5] text-gray-500 rounded-lg font-semibold text-sm cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Photo list */}
          <div className="space-y-4">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className={`bg-white border rounded-xl overflow-hidden transition-all ${
                  photo.is_active ? "border-gray-100/70" : "border-background-100 opacity-60"
                }`}
              >
                <div className="flex flex-col md:flex-row">
                  {/* Thumbnail */}
                  <div className="w-full md:w-64 h-40 md:h-auto flex-shrink-0 bg-[#FAF8F5]">
                    <img
                      src={photo.image_url}
                      alt={photo.alt_text}
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 p-5 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <p className="text-sm text-gray-500">
                          #{index + 1} &middot; Order: {photo.sort_order}
                        </p>
                        {!photo.is_active && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                            Hidden
                          </span>
                        )}
                      </div>
                      <p className="text-foreground-950 font-semibold text-sm mb-1 line-clamp-1">
                        {photo.caption_en}
                      </p>
                      <p className="text-xs text-gray-400 line-clamp-1">{photo.image_url}</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      <button
                        onClick={() => movePhoto(photo.id, "up")}
                        disabled={index === 0}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-100/70 text-gray-400 hover:bg-[#FAF8F5] cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move up"
                      >
                        <i className="ri-arrow-up-s-line"></i>
                      </button>
                      <button
                        onClick={() => movePhoto(photo.id, "down")}
                        disabled={index === photos.length - 1}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-100/70 text-gray-400 hover:bg-[#FAF8F5] cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Move down"
                      >
                        <i className="ri-arrow-down-s-line"></i>
                      </button>

                      <button
                        onClick={() => handleToggleActive(photo.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                          photo.is_active
                            ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                            : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        }`}
                      >
                        {photo.is_active ? "Hide" : "Show"}
                      </button>

                      <div className="flex-1"></div>

                      <button
                        onClick={() => handleEdit(photo)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#FAF8F5] text-gray-500 hover:bg-gray-100 cursor-pointer transition-colors whitespace-nowrap"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm("Are you sure you want to delete this photo? This action cannot be undone.")) {
                            handleDelete(photo.id);
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-600 hover:bg-rose-100 cursor-pointer transition-colors whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {photos.length === 0 && !loading && (
              <div className="text-center py-16 text-gray-400">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#FAF8F5] mx-auto mb-4">
                  <i className="ri-image-line text-2xl"></i>
                </div>
                <p>No photos yet. Click "Add Photo" to get started.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}